// The workbench's data, from the live API or from the demo fixture (/demo:
// GimasiLM in scenario A, the data of the approved layout A).
import type { Example, ProjectDocument, SizeReport, Revision, FileRef, Hints, InputFile, ItemState, Project, Proposal, ReviewItem, ReviewSummary, SampleResult, Turn, Validation, Version } from "../lib/types";

export interface Step {
  id: string;
  tool: string;
  text: string;
  state: "running" | "done" | "error";
}

/** The AI turn in progress (live events). */
export interface Live {
  running: boolean;
  text: string; // streamed words of the current answer
  steps: Step[];
  error: string;
}

export interface SetupState {
  scenario: "A" | "B" | "C";
  protoFiles: string[];
  messages: string[];
  error: string;
}

export interface WorkbenchData {
  demo: boolean;
  me: { name: string; initials: string; role: "admin" | "builder" | "viewer" | ""; org: string; region: string; storePublisher?: boolean; tokenStorage?: boolean };
  project: Project;
  files: FileRef[];
  inputs: InputFile[];
  items: ReviewItem[];
  summary: ReviewSummary;
  validation: Validation | null;
  turns: Turn[];
  live: Live;
  aiPaused: boolean;
  aiUsage: string;
  /** set when the package has no manifest yet (the set-up form shows). */
  setup: SetupState | null;
  /** scenario B: the requirements document (null for A and C). */
  requirements: ProjectDocument | null;
  /** logic types switch (GIOTHUB-31, /api/v1/config logic_types) and the known logic modules */
  logicTypes?: boolean;
  modules?: LogicModule[];
  /** the draft's manifest says protocol logic: its module and contract range */
  logic?: { module: string; version: string } | null;
  /** geo meaning tags (GIOTHUB-64, /me geo_tags): the picker's tags and the manifest's geolocation block */
  geoTags?: boolean;
  /** parameter classes on in this builder (GIOTHUB-118) */
  paramClass?: boolean;
  geoFamilies?: GeoFamily[];
  geolocation?: SetupGeo | null;
}

/** One family of geo meaning tags (GET /api/v1/geo-tags). */
export interface GeoFamily {
  family: string;
  label: string;
  parts: { tag: string; label: string; unit?: string }[];
}

/** A suggested geo tag for a field (GET /api/v1/geo-tags/suggest). */
export interface GeoSuggestion { tag: string; label: string; reason: string }

/** The manifest's geolocation block in the set-up form. */
export interface SetupGeo { enabled: boolean; inputs: string[]; reported?: boolean; uere_m?: number }

/** A logic module's descriptor (GET /api/v1/modules). */
export interface LogicModule {
  id: string;
  name: string;
  contract_majors: number[];
  description: string;
  connectivity?: { downlink: string; window_s?: number };
  params?: { properties?: Record<string, unknown> };
  config_keys?: Record<string, string>;
  examples?: { name: string; result: unknown }[];
}

export interface SetupForm {
  root: string;
  version: string;
  vendor: string;
  description: string;
  categories: string[];
  license: string;
  website?: string;
  support_email?: string;
  /** logic types switch on: protocol "logic" with its module, the downlink handling */
  protocol?: string;
  logic?: { module: string; version: string; params?: Record<string, unknown> };
  connectivity?: { downlink: string; window_s?: number };
  /** geo tags switch on: the manifest's geolocation block */
  geolocation?: SetupGeo;
}

export interface PublishingIn {
  target: string;
  git_url?: string;
  git_branch?: string;
  git_username?: string;
  git_provider?: string;
  git_auth?: "token" | "ssh";
  git_token?: string;
  git_key?: string;
  git_ca?: string;
  test?: boolean;
}

export interface Actions {
  decide(id: string, action: "accept" | "reject" | "adopt" | "answer" | "dismiss", answer?: string): Promise<void>;
  acceptHigh(keys: string[]): Promise<void>;
  validate(): Promise<void>;
  send(text: string): Promise<void>;
  uploadSources(files: File[]): Promise<void>;
  uploadInputs(files: File[]): Promise<void>;
  setup(form: SetupForm): Promise<void>;
  applyReview(): Promise<void>;
  /** person's edit of a proposal (state edited) */
  edit(id: string, proposal: Proposal): Promise<void>;
  readFile(path: string): Promise<string>;
  saveFile(path: string, text: string): Promise<void>;
  fieldInfo(field: string): Promise<FieldInfo | null>;
  /** geo tags switch: suggested tags from a field's name and unit (the author confirms) */
  geoSuggest(field: string, unit: string): Promise<GeoSuggestion[]>;
  vocabulary(): Promise<Vocabulary | null>;
  versions(): Promise<Version[]>;
  setPublishing(t: PublishingIn): Promise<void>;
  /** a new SSH deploy key: the public key to add, the sealed private key to send back */
  newDeployKey(): Promise<{ public_key: string; key: string }>;
  createVersion(version: string): Promise<void>;
  reviewVersion(version: string): Promise<void>;
  publishVersion(version: string, acceptIssues: boolean): Promise<void>;
  refreshVersion(version: string): Promise<void>;
  examples(): Promise<Example[]>;
  putExample(ex: Example): Promise<void>;
  deleteExample(name: string): Promise<void>;
  runExamples(preview: boolean): Promise<SampleResult[]>;
  revisions(): Promise<Revision[]>;
  // scenario B
  document(name: ProjectDocument["name"]): Promise<ProjectDocument>;
  putDocument(name: ProjectDocument["name"], content: string, version: number): Promise<ProjectDocument>;
  confirmRequirements(version: number): Promise<void>;
  sizes(): Promise<SizeReport | null>;
  firmwareZip(): Promise<Blob>;
}

/** One protobuf field as the store engine describes it (/inspect?field=). */
export interface FieldInfo {
  full_name: string;
  name: string;
  number: number;
  type: string;
  type_name?: string;
  cardinality: string;
  leading_comment?: string;
  trailing_comment?: string;
  effective: Hints;
  uses?: { key: string; classes?: string[]; type?: string; unit?: string; role?: string }[];
}

export interface Vocabulary {
  version: string;
  roles: { name: string; label: string; kind?: string; value_types: string[]; description?: string; deprecated?: boolean }[];
  kinds: { name: string; label: string; default: string; examples: string[] }[];
  qualifiers: { name: string; description: string; values?: string[] }[];
}

export const idleLive: Live = { running: false, text: "", steps: [], error: "" };

/** Short name of a protobuf message: glm.v1.StatusRecord -> StatusRecord. */
export function short(full: string): string {
  return full.split(".").pop() ?? full;
}

/** Review group of an item: the message its field belongs to. */
export function groupOf(it: ReviewItem): string {
  const p = it.proposal;
  if (p.commands) return "Commands";
  if (p.message) return short(p.message);
  if (p.field) {
    const parts = p.field.split(".");
    return parts[parts.length - 2] ?? p.field;
  }
  // scenario C: keys grouped by their first segment, plain keys together
  return it.key_path.includes(".") ? it.key_path.split(".")[0] : "Keys";
}

export function hintsOf(it: ReviewItem): Hints {
  return (it.state === "edited" && it.edited ? it.edited.hints : it.proposal.hints) ?? {};
}

/** A catalogue entry proposal (scenario C) rather than classification hints. */
export function isEntry(it: ReviewItem): boolean {
  return !it.proposal.field && !it.proposal.message && !!it.proposal.key;
}

/** The classes of a proposal: classification "class" or catalogue "classes". */
export function classesOf(h: Hints): string[] {
  return (h.class as string[] | undefined) ?? (h.classes as string[] | undefined) ?? [];
}

export interface Group {
  name: string;
  done: number;
  total: number;
  questions: number;
}

export function groups(items: ReviewItem[]): Group[] {
  const by = new Map<string, Group>();
  for (const it of items) {
    if (it.kind !== "key") continue;
    const name = groupOf(it);
    const g = by.get(name) ?? { name, done: 0, total: 0, questions: 0 };
    g.total++;
    if (it.state === "accepted" || it.state === "edited" || it.state === "rejected") g.done++;
    by.set(name, g);
  }
  for (const it of items) {
    if (it.kind === "question" && it.state === "open") {
      const key = items.find((k) => k.kind === "key" && (k.key_path === it.key_path || k.proposal.field === it.key_path));
      const g = key && by.get(groupOf(key));
      if (g) g.questions++;
    }
  }
  return [...by.values()];
}

export function hasQuestion(it: ReviewItem, items: ReviewItem[]): boolean {
  return items.some((q) => q.kind === "question" && q.state === "open" && (q.key_path === it.key_path || q.key_path === it.proposal.field));
}

export function shownState(it: ReviewItem, items: ReviewItem[]): ItemState | "question" {
  if (it.state === "proposed" && hasQuestion(it, items)) return "question";
  return it.state;
}

/** Steps of stored turns: every tool call with the summary of its result. */
export function storedSteps(turns: Turn[]): Map<number, Step[]> {
  const results = new Map<string, { summary: string; error: boolean }>();
  for (const t of turns)
    for (const b of t.content)
      if (b.type === "tool_result" && b.tool_use_id) results.set(b.tool_use_id, { summary: b._summary ?? "", error: !!b.is_error });
  const out = new Map<number, Step[]>();
  for (const t of turns) {
    if (t.role !== "assistant") continue;
    const steps = t.content
      .filter((b) => b.type === "tool_use")
      .map((b) => {
        const r = results.get(b.id ?? "");
        return { id: b.id ?? "", tool: b.name ?? "", text: r?.summary ?? "", state: (r?.error ? "error" : "done") as Step["state"] };
      });
    if (steps.length) out.set(t.id, steps);
  }
  return out;
}

// --- demo fixture -------------------------------------------------------

let n = 0;
const key = (
  field: string,
  keyPath: string,
  cls: string[],
  kind: string,
  unit: string,
  role: string,
  q: Record<string, string>,
  label: string,
  confidence: "high" | "medium" | "low",
  state: ItemState,
  reason: string,
): ReviewItem => ({
  id: `demo-${++n}`, kind: "key", key_path: keyPath, state, confidence, reason, question: "", answer: "",
  blocking: confidence === "low",
  proposal: { field: "glm.v1." + field, key: keyPath, kind, hints: { class: cls, unit: unit || undefined, role: role || undefined, qualifiers: q, label } },
});

const question = (key_path: string, text: string): ReviewItem => ({
  id: `demo-${++n}`, kind: "question", key_path, state: "open", proposal: { hints: {} }, confidence: null,
  reason: "", question: text, answer: "", blocking: true,
});

const NOW = "2026-10-10T14:02:00Z";
const ST = ["STATE"];
const TS = ["STATE", "TIMESERIES"];

function demoItems(): ReviewItem[] {
  const items: ReviewItem[] = [
    key("StatusRecord.battery_pct", "status.battery_pct", TS, "ratio", "%", "state_of_charge", { of: "battery" }, "Battery", "high", "accepted", "comment \"0..100\""),
    key("StatusRecord.battery_mv", "status.battery_mv", TS, "voltage", "mV", "voltage", { of: "battery" }, "Battery voltage under load", "high", "proposed", "unit in the name, comment \"under load\""),
    key("StatusRecord.battery_used_mah", "status.battery_used_mah", TS, "", "mA.h", "", {}, "Battery charge used", "low", "proposed", "mA.h: no role found in the vocabulary"),
    key("StatusRecord.battery_eos", "status.battery_eos", ST, "boolean", "", "", {}, "Battery end of service", "medium", "proposed", "comment \"end of service detected\""),
    key("StatusRecord.tilted", "status.tilted", TS, "boolean", "", "", {}, "Tilted", "medium", "edited", "boolean with an alert of the same name"),
    key("StatusRecord.tilt_deg", "status.tilt_deg", TS, "angle", "deg", "angle", { of: "installation" }, "Tilt from reference", "medium", "proposed", "comment \"angle from installed reference\""),
    key("StatusRecord.temperature_dc", "status.temperature_dc", TS, "temperature", "0.1.Cel", "temperature", { of: "device" }, "Device temperature", "high", "accepted", "comment \"0.1 degC\""),
    key("StatusRecord.door_link_up", "status.door_link_up", TS, "boolean", "", "", { of: "door_sensor" }, "Door sensor connected", "high", "accepted", "name"),
    key("StatusRecord.door_s1_battery_mv", "status.door_s1_battery_mv", TS, "voltage", "mV", "voltage", { of: "door_sensor" }, "Door sensor battery", "high", "proposed", "unit in the name"),
    key("StatusRecord.door_s1_rssi_dbm", "status.door_s1_rssi_dbm", TS, "power_level", "dB[mW]", "signal_strength", { of: "door_sensor" }, "Door sensor signal", "medium", "proposed", "unit in the name; 0 = not measured"),
    key("StatusRecord.fw_version", "status.fw_version", ST, "", "", "firmware_version", {}, "Firmware version", "high", "accepted", "comment \"semantic version\""),
    key("StatusRecord.uptime_s", "status.uptime_s", TS, "time", "s", "duration", { of: "uptime" }, "Uptime", "high", "accepted", "unit in the name"),
    key("StatusRecord.last_reset", "status.last_reset", ST, "", "", "", {}, "Last reset reason", "high", "proposed", "enum ResetReason"),
    key("StatusRecord.active_alerts", "status.active_alerts", ST, "", "", "", {}, "Active alerts", "low", "rejected", "bitmask of EventType: labels per bit unknown"),
    key("StatusRecord.attach_time_s", "status.attach_time_s", TS, "time", "s", "duration", { of: "lte_attach" }, "LTE attach time", "high", "proposed", "comment \"power-on to registered\""),
    key("CellInfo.rsrp_dbm", "status.cell.rsrp_dbm", TS, "power_level", "dB[mW]", "signal_strength", { of: "lte" }, "LTE signal (RSRP)", "high", "accepted", "unit in the name"),
    key("LevelRecord.distance_mm", "level.distance_mm", ["TIMESERIES"], "length", "mm", "distance", { of: "surface" }, "Distance to the surface", "high", "accepted", "comment"),
    key("LevelRecord.fill_pct", "level.fill_pct", ["TIMESERIES"], "ratio", "%", "fill_level", { of: "tank" }, "Fill level", "high", "accepted", "name"),
    key("DoorRecord.day_door_openings", "door.day_door_openings", ["TIMESERIES"], "count", "", "count", { of: "door_opening", period: "day" }, "Door openings today", "high", "accepted", "name"),
  ];
  items.push(
    question("status.battery_used_mah", "A charge in mA.h. I found no role for it in the vocabulary. Request a role from Gimasi, or keep the key without a role?"),
    question("status.door_s1_rssi_dbm", "The proto says 0 = \"not measured yet\". Declare 0 a no-data value (null_value \"0\"), so charts and rules skip it instead of reading 0 dBm?"),
  );
  return items;
}

export function demoData(): WorkbenchData {
  const items = demoItems();
  return {
    demo: true,
    me: { name: "Massimo Santoli", initials: "MS", role: "builder", org: "Gimasi SA", region: "EU", storePublisher: true, tokenStorage: true },
    project: {
      id: "demo", org_id: "gimasi", region: "eu", type_id: "ch.gimasi.lm", name: "GimasiLM", scenario: "A",
      publish_target: "store", git_url: "", head_rev: 37, created_by: "massimo", created_at: NOW, updated_at: NOW,
    },
    files: [
      "manifest.yaml", "proto/glm.proto", "classification.yaml", "catalogue.json", "ai_guide.md",
      "samples/boot_batch0.bin", "samples/records_batch.bin", "samples/tilt_event.bin", "CHANGELOG.md",
    ].map((path) => ({ path, sha256: "", size: 0 })),
    inputs: [
      { id: "i1", name: "GimasiLM Firmware Specification.pdf", content_type: "application/pdf", size: 1_840_000, created_at: NOW },
      { id: "i2", name: "GimasiLM MQTT Protocol Reference.pdf", content_type: "application/pdf", size: 912_000, created_at: NOW },
      { id: "i3", name: "boot_uplink_0613.bin", content_type: "application/octet-stream", size: 3_431, created_at: NOW },
    ],
    items,
    summary: { states: {}, blocking: 2 },
    validation: {
      id: 1, rev: 37, engine: "0.8.0", ok: true, errors: 0, warnings: 2, samples_ok: 8, samples_total: 8,
      duration_ms: 64, created_at: NOW,
      report: {
        findings: [
          { level: "warning", code: "roles.missing", path: "status.battery_used_mah", message: "No role on a TIMESERIES key of a public type." },
          { level: "warning", code: "catalogue.bits", path: "status.active_alerts", message: "Bitmask key without labels for its bits (EventType 1..31)." },
          { level: "info", code: "samples.ok", path: "samples/boot_batch0", message: "Decoded: Uplink batch_seq 76, 4 records, DeviceInfo present. Matches the expected values." },
          { level: "info", code: "proto.compiled", path: "proto/glm.proto", message: "Compiled: 31 messages, root glm.v1.Uplink." },
        ],
      },
    },
    turns: [
      { id: 1, role: "user", created_at: NOW, content: [{ type: "text", text: "Classify glm.proto for GimasiLM. Roots: Uplink and Downlink. The firmware specification and the MQTT protocol reference are attached." }] },
      { id: 2, role: "assistant", created_at: NOW, content: [
        { type: "tool_use", id: "s1", name: "inspect_proto" }, { type: "tool_use", id: "s2", name: "read_document" },
        { type: "tool_use", id: "s3", name: "vocabulary" }, { type: "tool_use", id: "s4", name: "propose_keys" },
        { type: "tool_use", id: "s5", name: "validate" }, { type: "tool_use", id: "s6", name: "run_samples" },
      ] },
      { id: 3, role: "user", created_at: NOW, content: [
        { type: "tool_result", tool_use_id: "s1", _summary: "glm.v1.Uplink · 31 messages, 214 plan rows · 1.2 s" },
        { type: "tool_result", tool_use_id: "s2", _summary: "MQTT Protocol Reference.pdf · 912 kB · 9.8 s" },
        { type: "tool_result", tool_use_id: "s3", _summary: "vocabulary 1.1.0 · 0.0 s" },
        { type: "tool_result", tool_use_id: "s4", _summary: "40 proposals stored, 0 refused · 41 s" },
        { type: "tool_result", tool_use_id: "s5", _summary: "0 errors, 2 warnings, samples 8/8 · 0.4 s" },
        { type: "tool_result", tool_use_id: "s6", _summary: "8 of 8 samples as expected · 0.1 s" },
      ] },
      { id: 4, role: "assistant", created_at: NOW, content: [{ type: "text", text: "I read the schema and both documents. 151 of 214 keys have a clear class, unit and role from the proto comments; the high-confidence rows of StatusRecord are ready for you, and two need an answer before I can classify them." }] },
    ],
    live: idleLive,
    aiPaused: false,
    aiUsage: "1.84 CHF",
    setup: null,
    requirements: null,
  };
}
